/**
 * SmartRedirect Component
 * 
 * Componente que redireciona automaticamente para a primeira página acessível
 * baseada nas permissões do usuário.
 * 
 * Se o usuário não tem acesso à rota atual, redireciona para a primeira
 * rota que ele tem permissão para acessar.
 */

import { Navigate, useLocation } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import { getFirstAccessibleRoute, isRouteAccessible } from '../utils/getFirstAccessibleRoute'

function SmartRedirect({ children, requiredModule, requiredAction, alsoModule, alsoAction }) {
  const location = useLocation()
  const { hasPermission, isAdmin, loading, permissions } = usePermissions()

  // Aguardar carregamento das permissões
  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
  }

  // Se não especificou módulo/ação, verificar se a rota atual é acessível
  if (!requiredModule || !requiredAction) {
    const currentRoute = location.pathname
    const isCurrentRouteAccessible = isRouteAccessible(currentRoute, hasPermission, isAdmin)

    if (!isCurrentRouteAccessible) {
      // Encontrar primeira rota acessível (rota completa com /erp/)
      const firstAccessible = getFirstAccessibleRoute(hasPermission, isAdmin, false)
      if (firstAccessible) {
        console.log(`[SmartRedirect] Rota atual (${currentRoute}) não acessível, redirecionando para: ${firstAccessible}`)
        return <Navigate to={firstAccessible} replace />
      }
      // Se não tem nenhuma rota acessível, redirecionar para home
      return <Navigate to="/" replace />
    }

    return children
  }

  // Se especificou módulo/ação, verificar permissão específica
  const hasAccess = hasPermission(requiredModule, requiredAction)
    || (alsoModule && alsoAction && hasPermission(alsoModule, alsoAction))
  
  // Logs de debug para rastrear verificação de permissões
  console.log(`[SmartRedirect] Verificando acesso à rota: ${location.pathname}`, {
    requiredModule,
    requiredAction,
    hasAccess,
    isAdmin: isAdmin,
    permissions: permissions[requiredModule] || {},
    specificPermission: permissions[requiredModule]?.[requiredAction]
  })

  if (!hasAccess) {
    // Encontrar primeira rota acessível (rota completa com /erp/)
    const firstAccessible = getFirstAccessibleRoute(hasPermission, isAdmin, false)
    if (firstAccessible) {
      console.log(`[SmartRedirect] Sem permissão para ${requiredModule}.${requiredAction}, redirecionando para: ${firstAccessible}`)
      return <Navigate to={firstAccessible} replace />
    }
    // Se não tem nenhuma rota acessível, redirecionar para home
    console.warn(`[SmartRedirect] Usuário sem permissão ${requiredModule}.${requiredAction} e sem rotas acessíveis, redirecionando para home`)
    return <Navigate to="/" replace />
  }

  console.log(`[SmartRedirect] Acesso permitido para ${requiredModule}.${requiredAction}`)
  return children
}

export default SmartRedirect

